import React, { useEffect, useState, useRef } from "react";
import {
  TextField, Button, Grid, Box, Switch, FormControlLabel, FormControl,
} from "@mui/material";
import { useAuth, useSnackbar, useLoading, useNavigation } from "../../../Context/Contexts";
import CenteredCard from "../../../components/Card/CenteredCardLayout";
import { LanguageViewModel } from "../../../Types/ExportTypes";
import DefaultActions from "../../../components/Card/DefaultActions";
import { useTranslation } from "react-i18next";
import { ToolbarWithSaveHandle } from "../../../components/Toolbar/ToolbarWithSave";

const LanguageEditPage: React.FC<{ params?: { [key: string]: any } }> = ({ params }) => {
  const { code = "" } = params || {};

  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const { showLoading, hideLoading } = useLoading();
  const { authService } = useAuth();
  const { showSnackbar } = useSnackbar();
  const { goBack, navigate } = useNavigation();
  const [viewModel, setViewModel] = useState<(LanguageViewModel) | null>(null);
  const toolbarRef = useRef<ToolbarWithSaveHandle>(null);
  const showHasChangesError = () => {
    toolbarRef.current?.showHasChangesError();
  };
  useEffect(() => {
    if (isLoading) showLoading();
    else hideLoading();
  }, [isLoading, showLoading, hideLoading]);

  useEffect(() => {
    if (code) {
      setIsLoading(true);
      authService
        .get<LanguageViewModel>(`/settingsApi/GetLanguage?code=${code}`)
        .then((response) =>
          setViewModel(response ? { ...response, originalValues: { ...response }, hasChanges: false } : null)
        )
        .catch((error: any) => {
          showSnackbar(`${t("common.failedToFetchEntries")}: ${error.response?.data ?? error.message}`, "error");
        })
        .finally(() => setIsLoading(false));
    } else {
      const defaultLanguage: LanguageViewModel = {
        code: "",
        name: "",
        isCreating: true,
        originalValues: {},
        hasChanges: false,
      };
      defaultLanguage.originalValues = { ...defaultLanguage };
      setViewModel(defaultLanguage);
    }
  }, [code, authService]);

  const handleSave = async () => {
    if (!viewModel) return;
    if (!validateLanguage()) return; // Prevent save if validation fails
    var newItem = viewModel.isCreating ?? false;

    setIsLoading(true);
    try {
      const response = await authService.post<LanguageViewModel>("/settingsApi/CreateOrUpdateLanguage", viewModel);
      setViewModel({ ...response, originalValues: { ...response }, hasChanges: false });
      showSnackbar(t("dialog.savedSuccessfully"), "success");

      if (newItem) {
        navigate({ path: "/settings/language/edit", params: { code: response.code } });
      }
    } catch {
      showSnackbar(t("dialog.saveErrorGeneric"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field: keyof LanguageViewModel, value: any) => {
    setViewModel((prev) => (prev ? { ...prev, [field]: value, hasChanges: true } : null));
  };

  const handleCancelChanges = () => {
    setViewModel((prev) => {
      if (!prev || !prev.originalValues) return prev;
      const original = prev.originalValues as LanguageViewModel;
      return { ...original, originalValues: { ...original }, hasChanges: false };
    });
  };


  const handleDelete = async () => {
    if (viewModel?.hasChanges) showHasChangesError();

    const isConfirmed = window.confirm(t("dialog.deleteEntityConfirm"));
    if (!isConfirmed) return;
    var newItem = viewModel?.isCreating ?? false;
    if (newItem) {
      navigate({ path: "/settings/language/list" });
      return;
    }

    setIsLoading(true);
    try {
      await authService.post(`/settingsApi/DeleteLanguage`, viewModel);
      showSnackbar(t("dialog.deletedSuccessfully"), "success");

      navigate({ path: "/settings/language/list" });
    } catch (error: any) {
      showSnackbar(`${t("dialog.deleteFailed")} : ${error.response?.data ?? error.message}`, "error");

    } finally {
      setIsLoading(false);
    }
  };

  const validateLanguage = () => {
    if (!viewModel) {
      showSnackbar(t("dialog.dataMissing"), "error");
      return false;
    }

    if (!viewModel.code || viewModel.code.trim() === "") {
      showSnackbar(t("help.codeRequired"), "warning");
      return false;
    }

    if (!viewModel.name || viewModel.name.trim() === "") {
      showSnackbar(t("help.nameRequired"), "warning");
      return false;
    }


    return true;
  };
  return (
    <>

      <CenteredCard
        ref={toolbarRef}
        title={t("common.edit") + " " + t("common.language")}
        isLoading={isLoading}
        handleSave={handleSave}
        handleCancel={handleCancelChanges}
        hasChanges={viewModel?.hasChanges ?? false}
      >
        <Box sx={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          maxWidth: "fit-content",
          mb: 2
        }}>
          <DefaultActions
            onSave={handleSave}
            onDelete={handleDelete}
          />
        </Box>
        {viewModel && (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label={t("common.code")}
                  value={viewModel.code || ""}
                  onChange={(e) => handleChange("code", e.target.value)}
                  fullWidth
                  margin="normal"
                  disabled={viewModel.isCreating != true}
                  error={!viewModel.code || viewModel.code.trim() === ""}
                  helperText={!viewModel.code || viewModel.code.trim() === "" ? t("help.codeRequired") : ""}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label={t("common.name")}
                  value={viewModel.name || ""}
                  onChange={(e) => handleChange("name", e.target.value)}
                  fullWidth
                  margin="normal"
                  error={!viewModel.name || viewModel.name.trim() === ""}
                  helperText={!viewModel.name || viewModel.name.trim() === "" ? t("help.nameRequired") : ""}
                />
              </Grid>
              {/*<Grid item xs={12}>*/}
              {/*    <TextField*/}
              {/*        label="External Reference"*/}
              {/*        value={language.externalReference || ""}*/}
              {/*        onChange={(e) => handleChange("externalReference", e.target.value)}*/}
              {/*        fullWidth*/}
              {/*        margin="normal"*/}
              {/*    />*/}
              {/*</Grid>*/}
              {/*<Grid item xs={12}>*/}
              {/*    <TextField*/}
              {/*        label="External Key"*/}
              {/*        value={language.externalKey || ""}*/}
              {/*        onChange={(e) => handleChange("externalKey", e.target.value)}*/}
              {/*        fullWidth*/}
              {/*        margin="normal"*/}
              {/*    />*/}
              {/*</Grid>*/}

            </Grid>


          </>
        )}

        <Box sx={{ marginTop: 2, textAlign: "center" }}>
          <Button variant="contained" onClick={handleSave} disabled={isLoading || !viewModel?.hasChanges} sx={{ marginRight: 2 }}>
            {t("common.save")}
          </Button>
          <Button variant="outlined" onClick={handleCancelChanges} disabled={!viewModel?.hasChanges} sx={{ marginRight: 2 }}>
            {t("common.cancel")}
          </Button>
          <Button variant="outlined" onClick={goBack}>
            {t("common.back")}
          </Button>
        </Box>

      </CenteredCard>


    </>
  );
};

export default LanguageEditPage;
